import type { AxiosRequestConfig } from "axios";
import mitt, { type Emitter } from "mitt";
import { defineStore } from "pinia";
import type { Ref, UnwrapNestedRefs } from "vue";
import { reactive, ref } from "vue";
import { useLanguageStore } from "../../backend/stores/languageStore.ts";
import type { ProductVariantResponse } from "../../responses/ProductVariantResponse";
import { api } from "../scripts/api";
import { useFilterStore } from "./filter";
import { useGeoLocationStore } from "./geolocation";

type Events = {
    monthChanged: number;
};
export const useCourseStore = defineStore("course", () => {
    const languageStore = useLanguageStore();
    const filterStore = useFilterStore();
    const geoLocationStore = useGeoLocationStore();
    const courses: Ref<ProductVariantResponse[]> = ref([]);
    const totalCount = ref(0);
    const search: Ref<string> = ref(
        sessionStorage.getItem("location_search") ?? "",
    );
    const toggleAll: Ref<boolean> = ref(false);
    const loading: Ref<boolean> = ref(false);
    const month: Ref<number> = ref(
        sessionStorage.getItem("location_month") ?? "0",
    );
    const events: Emitter<Events> = mitt<Events>();

    const getProductVariantRequest: UnwrapNestedRefs<{
        timeout: number | null;
        abort: AbortController | null;
    }> = reactive({
        timeout: null,
        abort: null,
    });

    const getCourses = async (productId) => {
        return new Promise((resolve, reject) => {
            if (getProductVariantRequest.abort !== null) {
                getProductVariantRequest.abort.abort();
            }

            getProductVariantRequest.abort = new AbortController();

            try {
                loading.value = true;

                filterStore.toUrlString(null, false, true);
                api()
                    .post(
                        `/site/product-variants/`,
                        {
                            product_id: productId,
                            lang: languageStore.currentIso(),
                            search: geoLocationStore.searchQuery,
                            radius: geoLocationStore.radius,
                            month: month.value,
                            all: toggleAll.value,
                        },
                        {
                            signal: getProductVariantRequest.abort.signal,
                        } as AxiosRequestConfig,
                    )
                    .then((response) => {
                        setCourses(response.data.data);
                        totalCount.value = response.data.total_count;
                        loading.value = false;
                        resolve(response.data.data);
                    });
            } catch (e) {
                reject(e);
            }
        });
    };

    const setCourses = (variants: ProductVariantResponse[]) => {
        courses.value = variants;
    };

    const setMonth = (newMonth: number) => {
        month.value = newMonth;
        sessionStorage.setItem("location_month", newMonth);
        filterStore.setFilterValue("maand", newMonth, true);

        events.emit("monthChanged", newMonth);
    };

    const getRemainingMonths = () => {
        const now = new Date();

        const formatter = new Intl.DateTimeFormat(languageStore.currentIso(), {
            month: "long",
        });

        return Array.from({ length: 12 }, (_, i) => {
            const date = new Date(now.getFullYear(), now.getMonth() + i, 1);

            return {
                value: date.getMonth() + 1,
                year: date.getFullYear(),
                label: formatter.format(date),
            };
        });
    };

    return {
        courses,
        search,
        month,
        totalCount,
        toggleAll,
        loading,
        setMonth,
        getCourses,
        setCourses,
        getRemainingMonths,
        events,
    };
});
